/**
 * "What should we play?" — the table's own vote on the next felt.
 *
 * The host opens a poll between hands, every player at the table picks one of
 * the games on the ballot, and the winner becomes the game. The tally is pure:
 * the server holds the ballots, this decides what they add up to, and the TV,
 * the phone and the tests all read the same rows.
 */
import { GAME_LABELS, GAME_TYPES, type GameType } from './types.js';

/** One player's ballot, as everyone at the table sees it. */
export interface PublicGamePollVote {
  playerId: string;
  name: string;
  game: GameType;
}

/** One line of the tally: an option, its count, and where it stands. */
export interface GamePollTally {
  game: GameType;
  label: string;
  votes: number;
  /** Share of the ballots cast so far, 0–1. Zero while nobody has voted. */
  share: number;
  /** True for the option that would win if the poll ended right now. */
  leading: boolean;
}

/** A live poll, as it rides in `RoomState`. */
export interface PublicGamePoll {
  /** The games on the ballot, in the order they are offered. */
  options: GameType[];
  /** Every option with its count, most votes first. */
  tallies: GamePollTally[];
  /** Who voted for what. Empty until the first ballot lands. */
  votes: PublicGamePollVote[];
  /** Ballots cast. */
  cast: number;
  /** Players who could still vote — connected, seated, and yet to pick. */
  pending: number;
  /** The player who opened the poll, for the "X asked" line. */
  openedBy: string | null;
  /** The option that would win if the poll ended now. Null before any vote. */
  winner: GameType | null;
}

/** What ending a poll settled on: the game now on the felt, or nothing. */
export interface GamePollResult {
  /** The winner the host applied, or null when the poll was dropped. */
  game: GameType | null;
  /** Ballots the decision rested on. */
  votes: number;
}

/** The smallest ballot worth putting to a table. */
export const MIN_POLL_OPTIONS = 2;

/**
 * Clean a requested ballot: keep only real games, drop duplicates, and hold
 * `GAME_TYPES` order so every picker reads the same left to right. An empty or
 * missing request means the whole catalog.
 */
export function normalizePollOptions(requested: unknown): GameType[] {
  if (requested === undefined || requested === null) return [...GAME_TYPES];
  if (!Array.isArray(requested)) return [];
  const wanted = new Set(requested);
  const options = GAME_TYPES.filter((game) => wanted.has(game));
  return options.length === 0 && requested.length === 0 ? [...GAME_TYPES] : options;
}

/**
 * Count the ballots.
 *
 * Ties break toward `current` when it is one of the tied leaders — a table
 * that cannot agree keeps dealing what it is dealing — and otherwise toward
 * the option that appears first on the ballot. Deterministic either way: no
 * coin flip decides what the room plays next.
 */
export function tallyGamePoll(
  options: readonly GameType[],
  ballots: readonly { game: GameType }[],
  current: GameType | null = null,
): { tallies: GamePollTally[]; winner: GameType | null } {
  const counts = new Map<GameType, number>(options.map((game) => [game, 0]));
  let cast = 0;
  for (const ballot of ballots) {
    const count = counts.get(ballot.game);
    if (count === undefined) continue;
    counts.set(ballot.game, count + 1);
    cast += 1;
  }

  const top = Math.max(0, ...counts.values());
  const tied = options.filter((game) => counts.get(game) === top);
  const winner = top === 0 ? null : tied.includes(current as GameType) ? current : (tied[0] ?? null);

  // Ballot order breaks display ties too, so a re-render never reshuffles two
  // options sitting on the same count.
  const tallies = options
    .map((game, index) => ({
      game,
      label: GAME_LABELS[game],
      votes: counts.get(game) ?? 0,
      share: cast === 0 ? 0 : (counts.get(game) ?? 0) / cast,
      leading: game === winner,
      index,
    }))
    .sort((a, b) => b.votes - a.votes || a.index - b.index)
    .map(({ index: _index, ...row }) => row);

  return { tallies, winner };
}
